import React, { useEffect, useState, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import Shell from "@/components/Shell";
import { api } from "@/lib/api";
import { Loader2, BadgeCheck, AlertTriangle, ArrowLeft } from "lucide-react";

// Poll the backend for checkout status after Stripe redirects back.
// Per integration playbook: poll every 2s, up to 5 attempts, then surface
// whatever state is on the record.
export default function BillingReturn() {
    const [params] = useSearchParams();
    const nav = useNavigate();
    const sessionId = params.get("session_id");
    const [status, setStatus] = useState("polling");
    const [data, setData] = useState(null);
    const [err, setErr] = useState("");
    const attempts = useRef(0);

    useEffect(() => {
        if (!sessionId) {
            setErr("No session_id in return URL");
            setStatus("error");
            return;
        }
        let stopped = false;

        const poll = async () => {
            if (stopped) return;
            attempts.current += 1;
            try {
                const { data: d } = await api.get(`/payments/checkout/status/${sessionId}`);
                if (stopped) return;
                setData(d);
                if (d.payment_status === "paid") {
                    setStatus("paid");
                    return;
                }
                if (d.status === "expired" || d.payment_status === "failed") {
                    setStatus("failed");
                    return;
                }
                if (attempts.current >= 6) {
                    setStatus("pending");
                    return;
                }
                setTimeout(poll, 2000);
            } catch (e) {
                if (stopped) return;
                setErr(`[HTTP ${e.response?.status || "?"}] ${e.response?.data?.detail || e.message}`);
                setStatus("error");
            }
        };
        poll();
        return () => { stopped = true; };
    }, [sessionId]);

    return (
        <Shell>
            <div className="max-w-xl mx-auto mt-8">
                <div className="paper-card p-8 text-center" data-testid="billing-return-card">
                    {status === "polling" && (
                        <>
                            <Loader2 className="w-8 h-8 animate-spin mx-auto text-stamp-red mb-4" />
                            <h2 className="font-heading text-2xl uppercase tracking-[0.08em] text-ink">
                                Filing the receipt…
                            </h2>
                            <p className="font-body text-sm text-ink-soft mt-3">
                                Stripe is confirming the payment. This usually takes a couple of seconds.
                            </p>
                        </>
                    )}
                    {status === "paid" && (
                        <>
                            <BadgeCheck className="w-12 h-12 text-stamp-green mx-auto mb-4" />
                            <h2 className="font-heading text-2xl uppercase tracking-[0.08em] text-ink">
                                Paid · receipt filed
                            </h2>
                            {data?.tier && data?.entitlement_applied && (
                                <p className="font-body text-base text-ink mt-3" data-testid="billing-return-tier">
                                    Tier upgraded to <span className="text-stamp-red font-heading uppercase tracking-[0.1em]">{data.tier}</span>
                                </p>
                            )}
                            {data?.amount_total && (
                                <p className="font-body text-sm text-ink-soft mt-2">
                                    {new Intl.NumberFormat(undefined, { style: "currency", currency: (data.currency || "GBP").toUpperCase() }).format((data.amount_total || 0) / 100)}
                                    {data?.stripe_mode === "test" ? " · test mode (no real money moved)" : ""}
                                </p>
                            )}
                            <button
                                onClick={() => nav("/billing")}
                                data-testid="billing-return-done"
                                className="mt-6 font-heading uppercase text-xs tracking-[0.25em] bg-ink text-paper hover:bg-stamp-red px-5 py-3 inline-flex items-center gap-2"
                            >
                                <ArrowLeft className="w-3 h-3" />
                                Back to billing
                            </button>
                        </>
                    )}
                    {status === "failed" && (
                        <>
                            <AlertTriangle className="w-10 h-10 text-stamp-red mx-auto mb-4" />
                            <h2 className="font-heading text-2xl uppercase tracking-[0.08em] text-stamp-red">
                                Payment did not complete
                            </h2>
                            <p className="font-body text-sm text-ink-soft mt-3">
                                Status: <span className="text-ink">{data?.payment_status || data?.status || "unknown"}</span>.
                                You can retry from the Billing page.
                            </p>
                            <button
                                onClick={() => nav("/billing")}
                                className="mt-6 font-heading uppercase text-xs tracking-[0.25em] bg-ink text-paper hover:bg-stamp-red px-5 py-3"
                            >
                                Try again
                            </button>
                        </>
                    )}
                    {status === "pending" && (
                        <>
                            <Loader2 className="w-8 h-8 animate-spin mx-auto text-ink-soft mb-4" />
                            <h2 className="font-heading text-2xl uppercase tracking-[0.08em] text-ink">
                                Still processing
                            </h2>
                            <p className="font-body text-sm text-ink-soft mt-3">
                                Stripe is taking longer than usual to confirm. Refresh in a minute,
                                or check the Billing page later — the receipt will land.
                            </p>
                        </>
                    )}
                    {status === "error" && (
                        <>
                            <AlertTriangle className="w-10 h-10 text-stamp-red mx-auto mb-4" />
                            <h2 className="font-heading text-2xl uppercase tracking-[0.08em] text-stamp-red">
                                Could not confirm
                            </h2>
                            <pre className="font-body text-xs text-ink-soft whitespace-pre-wrap break-words mt-3 text-left">{err}</pre>
                        </>
                    )}
                </div>
            </div>
        </Shell>
    );
}
